import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { computed, observable, action } from 'mobx'
import { of, type Observable } from 'rxjs'
import { Id } from '@/infra/id'
import { generateAvatarFallback } from '@/utils/image-util'
import { asAtom } from '@/utils/mobx-util'

export type UserProps = ModelProps<User>

const staffEmailDomains = ['@taxfyle.com', '@worklayer.com', '@mytps.com']

export class User extends Model<User> {
  id = Id.none
  publicId = Id.none
  authId = Id.none
  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor picture = ''
  @observable accessor platformAdmin = false

  // NOTE: These will only be populated for the current user
  // or if the caller is a platform admin.
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor multiFactorAuthenticationEnabled = false

  /**
   * Backing field for being fully loaded.
   *
   * @private
   */
  @observable private accessor _isFullyLoaded = false

  /**
   * The use of `asAtom` ensures the online status is only synced while there
   * are observers on {@link online}.
   */
  private readonly _online

  /**
   * Whether the user has been fully loaded from IAM or if we only have
   * potentially stale properties.
   */
  @computed
  get isFullyLoaded() {
    return this._isFullyLoaded
  }

  /**
   * Default value to use for users.
   */
  static get none() {
    return lazyNone()
  }

  constructor(
    private readonly _isCurrentUser: (user: User) => boolean,
    online$: Observable<boolean>,
  ) {
    super()
    this._online = asAtom(online$, false)
  }

  /**
   * Whether the user is currently online.
   */
  @computed
  get online() {
    return this._online()
  }

  /**
   * Avatar.
   */
  @computed
  get avatar() {
    return this.picture || this.avatarFallback
  }

  /**
   * Avatar fallback.
   */
  @computed
  get avatarFallback() {
    return generateAvatarFallback(this.displayName || this.email)
  }

  /**
   * Whether this is the current user.
   */
  @computed
  get isCurrentUser() {
    return this._isCurrentUser(this)
  }

  /**
   * Whether the user is staff, going by their email domain.
   */
  @computed
  get isStaff() {
    return staffEmailDomains.some((domain) => this.email.includes(domain))
  }

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return `${this.givenName} ${this.familyName}`
  }

  /**
   * Marks the user as fully loaded.
   */
  @action.bound
  markFullyLoaded() {
    this._isFullyLoaded = true
  }

  /**
   * Marks the user as having Multi-factor Authentication enabled.
   */
  @action.bound
  markMultiFactorAuthEnabled() {
    this.multiFactorAuthenticationEnabled = true
  }
}

const lazyNone = once(() => new User(() => false, of(false)))
